import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { NumericFormat, numericFormatter } from "react-number-format";
import {
  ChevronDownIcon,
  DocumentReportIcon,
  ExclamationIcon,
  PlusIcon,
} from "@heroicons/react/outline";

import TableSkeleton from "../../../../../Components/TableSkeleton";
import { classNames } from "../../../../../Utils";
import { fromPeriod, isPeriod } from "../../../../../Utils/calendarDate";
import {
  accountLabel,
  formatRecapDate,
  isCredited,
  sortReview,
  sumAmounts,
} from "./utils";

const RUPIAH = {
  thousandSeparator: ".",
  decimalSeparator: ",",
  decimalScale: 2,
  fixedDecimalScale: true,
};

// Tax figures are shown to the sen, never abbreviated: this is what the user
// copies into their return.
export const Money = ({ value, prefix = "" }) => (
  <NumericFormat
    displayType="text"
    value={Number(value) || 0}
    prefix={prefix}
    {...RUPIAH}
  />
);

export const formatRupiah = (value) =>
  numericFormatter(String(Number(value) || 0), { ...RUPIAH, prefix: "Rp" });

export const formatPeriod = (period, language, format = "MMM YYYY") =>
  isPeriod(period)
    ? dayjs(fromPeriod(period)).locale(language).format(format)
    : period || "-";

const Badge = ({ className, children }) => (
  <span
    className={classNames(
      "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
      className,
    )}
  >
    {children}
  </span>
);

const BADGE_NEUTRAL = "bg-gray-100 text-gray-700 ring-gray-500/20";
const BADGE_DEPOSIT = "bg-purple-50 text-purple-800 ring-purple-600/20";

// The deduction note on a settlement row, in plain figures like the rest of
// the sentence: "DPP invoice 275.000 − uang muka 150.000".
const plainAmount = (value) =>
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(
    Number(value) || 0,
  );

/* ---------------------------------------------------------------- Summary */

const SummaryItem = ({ label, value, footer, className }) => (
  <div className={classNames("min-w-0 bg-white px-4 py-4", className)}>
    <dt className="text-sm text-gray-600">{label}</dt>
    {/* Wraps between digit groups rather than overflowing if a figure is
        ever wider than its column; it is never cut or abbreviated. */}
    <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900 [overflow-wrap:anywhere]">
      {value}
    </dd>
    {footer && <dd className="mt-1 text-sm text-gray-600">{footer}</dd>}
  </div>
);

export const SummaryCards = ({ recap, view, creditedBillCount }) => {
  const { t } = useTranslation();
  const luxuryTax = Number(recap?.output?.luxuryTax) || 0;
  const showLuxury = luxuryTax !== 0;

  return (
    <dl
      aria-label={t("reports.vatRecap.summary.label")}
      className={classNames(
        "grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 shadow-sm sm:grid-cols-2",
        // Four across waits for xl: from lg the sidebar takes a third of the
        // width, and a quarter of what is left breaks Rp12.345.678.901,23
        // over two lines.
        showLuxury ? "xl:grid-cols-4" : "lg:grid-cols-3",
      )}
    >
      <SummaryItem
        label={t("reports.vatRecap.summary.output")}
        value={<Money value={recap?.output?.vat} prefix="Rp" />}
        footer={t("reports.vatRecap.summary.invoiceCount", {
          count: recap?.output?.invoices?.length || 0,
        })}
      />
      <SummaryItem
        label={t("reports.vatRecap.summary.input")}
        value={<Money value={recap?.input?.vat} prefix="Rp" />}
        footer={t("reports.vatRecap.summary.billCount", {
          count: creditedBillCount,
        })}
      />
      {showLuxury && (
        <SummaryItem
          label={t("reports.vatRecap.summary.luxury")}
          value={<Money value={luxuryTax} prefix="Rp" />}
        />
      )}
      <SummaryItem
        className={classNames(
          view.cardClass,
          // Three cards on a two-column row would leave a hole; the net
          // figure takes the full second row instead.
          !showLuxury && "sm:col-span-2 lg:col-span-1",
        )}
        label={t(view.label)}
        value={<Money value={view.amount} prefix="Rp" />}
        footer={<Badge className={view.badgeClass}>{t(view.badge)}</Badge>}
      />
    </dl>
  );
};

export const SummarySkeleton = () => (
  <div
    aria-hidden="true"
    className="grid animate-pulse grid-cols-1 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 sm:grid-cols-2 lg:grid-cols-4"
  >
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="bg-white px-4 py-4">
        <div className="h-3 w-24 rounded-md bg-gray-200" />
        <div className="mt-3 h-5 w-32 rounded-md bg-gray-300" />
        <div className="mt-3 h-3 w-16 rounded-md bg-gray-200" />
      </div>
    ))}
  </div>
);

/* ----------------------------------------------------------- Ledger check */

export const LedgerNotice = ({ differences }) => {
  const { t } = useTranslation();
  if (!differences.length) return null;

  return (
    <div className="flex gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
      <ExclamationIcon
        className="h-5 w-5 flex-shrink-0 text-amber-400"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <div className="min-w-0 text-sm text-amber-800">
        {differences.map(({ key, difference }) => (
          <p key={key} className="font-medium">
            {t(`reports.vatRecap.ledger.${key}`, {
              amount: formatRupiah(Math.abs(difference)),
            })}
          </p>
        ))}
        <p className="mt-1">{t("reports.vatRecap.ledger.hint")}</p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ Review list */

const REVIEW_PREVIEW = 3;

export const ReviewPanel = ({ review }) => {
  const { t } = useTranslation();
  const listId = useId();
  const rows = sortReview(review);
  // Open by default only when a tax is being left out: that changes the figure
  // above. A list of taxes that were counted anyway can wait behind a click.
  const [open, setOpen] = useState(() => rows.some((row) => !row.counted));
  const [showAll, setShowAll] = useState(false);

  if (!rows.length) return null;

  const visible = showAll ? rows : rows.slice(0, REVIEW_PREVIEW);
  const hidden = rows.length - visible.length;

  return (
    <section className="overflow-hidden rounded-lg border border-amber-200">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={listId}
          className="flex w-full items-center justify-between gap-4 bg-amber-50 px-4 py-3 text-left text-sm hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
        >
          <span className="font-medium text-amber-900">
            {t("reports.vatRecap.review.title")}
            <span className="font-normal text-amber-800">
              {" · "}
              {t("reports.vatRecap.review.count", { count: rows.length })}
            </span>
          </span>
          <span className="flex flex-shrink-0 items-center gap-1 font-medium text-blue-700">
            {open
              ? t("reports.vatRecap.review.hide")
              : t("reports.vatRecap.review.show")}
            <ChevronDownIcon
              className={classNames(
                "h-4 w-4 transition-transform duration-150 motion-reduce:transition-none",
                open && "rotate-180",
              )}
              aria-hidden="true"
            />
          </span>
        </button>
      </h3>
      {open && (
        <ul
          id={listId}
          className="divide-y divide-amber-100 border-t border-amber-200 bg-white"
        >
          {visible.map((row, index) => (
            <li
              key={`${row.taxID}-${index}`}
              className="grid gap-x-4 gap-y-1 px-4 py-3 text-sm sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto] sm:items-center"
            >
              <span className="font-medium text-gray-900 [overflow-wrap:anywhere]">
                {row.name}
              </span>
              <span className="text-gray-600">{row.message}</span>
              <span className="mt-1 flex items-center gap-4 sm:mt-0 sm:justify-end">
                <Badge
                  className={
                    row.counted
                      ? "bg-blue-50 text-blue-700 ring-blue-600/20"
                      : BADGE_NEUTRAL
                  }
                >
                  {row.counted
                    ? t("reports.vatRecap.review.counted")
                    : t("reports.vatRecap.review.notCounted")}
                </Badge>
                {row.taxID && (
                  <Link
                    to={`/v2/accounting/taxes?taxId=${encodeURIComponent(row.taxID)}`}
                    className="whitespace-nowrap py-1 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {t("reports.vatRecap.review.configure")}
                  </Link>
                )}
              </span>
            </li>
          ))}
          {(hidden > 0 || showAll) && rows.length > REVIEW_PREVIEW && (
            <li>
              <button
                type="button"
                onClick={() => setShowAll((value) => !value)}
                aria-controls={listId}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
              >
                {showAll
                  ? t("reports.vatRecap.review.less")
                  : `+ ${t("reports.vatRecap.review.more", { count: hidden })}`}
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

/* ----------------------------------------------------------------- Tables */

const TH =
  "px-3 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap";
const TH_NUM = `${TH} text-right`;
const TD = "px-3 py-2 text-sm whitespace-nowrap";
const TD_NUM = `${TD} text-right tabular-nums`;

const Section = ({ title, hint, children, as: Heading = "h3" }) => {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
        <Heading
          id={headingId}
          className="text-base font-semibold text-gray-900"
        >
          {title}
        </Heading>
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
      </div>
      {/* The frame scrolls with the table, so a wide table on a phone keeps
          its border instead of sliding out from under it. */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        {children}
      </div>
    </section>
  );
};

const NoRows = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-6 text-center text-sm text-gray-500">
      {children}
    </td>
  </tr>
);

// NPWP sits under the name rather than in a column of its own: most rows
// have a short one or none, and the spare width keeps the figure columns on
// screen at desktop without scrolling.
const Partner = ({ partner }) => (
  <td className={`${TD} min-w-[9rem] whitespace-normal`}>
    <span className="block">{partner?.name || "-"}</span>
    {partner?.taxNumber && (
      <span className="block text-xs tabular-nums text-gray-500">
        {partner.taxNumber}
      </span>
    )}
  </td>
);

const OUTPUT_COLUMNS = [
  { width: "w-20" },
  { width: "w-14" },
  { width: "w-32" },
  { align: "right", width: "w-24" },
  { align: "right", width: "w-24" },
  { align: "right", width: "w-20" },
  { align: "right", width: "w-12" },
];

export const OutputTable = ({ output, loading }) => {
  const { t } = useTranslation();
  const invoices = output?.invoices || [];
  const totals = output?.totals || {};

  return (
    <Section
      title={t("reports.vatRecap.output.title")}
      hint={t("reports.vatRecap.output.hint")}
    >
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.date")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.invoiceNumber")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.partner")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.taxBase")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.otherTaxBase")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.vat")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.luxuryTax")}</th>
          </tr>
        </thead>
        {loading ? (
          <TableSkeleton rows={4} columns={7} columnWidths={OUTPUT_COLUMNS} />
        ) : (
          <>
            <tbody className="divide-y divide-gray-200 bg-white text-gray-700">
              {invoices.length ? (
                invoices.map((invoice) => {
                  // A deposit is reported in the month it is received; the
                  // invoice that uses it later (a settlement) reports only
                  // what is left, so the month's VAT is never counted twice.
                  const isDeposit = invoice.kind === "DEPOSIT";
                  const isSettlement = invoice.kind === "SETTLEMENT";
                  return (
                  <tr key={invoice.id}>
                    <td className={TD}>{formatRecapDate(invoice.date)}</td>
                    <td className={TD}>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Link
                          to={
                            isDeposit
                              ? `/v2/accounting/incomes?tab=deposits&depositId=${encodeURIComponent(invoice.id)}`
                              : `/v2/accounting/incomes?invoiceId=${encodeURIComponent(invoice.id)}`
                          }
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {invoice.invoiceNumber || "-"}
                        </Link>
                        {isDeposit && (
                          <Badge className={BADGE_DEPOSIT}>
                            {t("deposits.vatRecap.deposit")}
                          </Badge>
                        )}
                        {isSettlement && (
                          <Badge className={BADGE_NEUTRAL}>
                            {t("deposits.vatRecap.settlement")}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <Partner partner={invoice.partner} />
                    <td className={TD_NUM}>
                      <Money value={invoice.taxBase} />
                      {isSettlement && invoice.deposit && (
                        <span className="block whitespace-normal text-xs font-normal text-gray-500">
                          {t("deposits.vatRecap.settlementNote", {
                            interpolation: { escapeValue: false },
                            invoice: plainAmount(
                              (Number(invoice.taxBase) || 0) +
                                (Number(invoice.deposit.taxBase) || 0),
                            ),
                            deposit: plainAmount(invoice.deposit.taxBase),
                          })}
                        </span>
                      )}
                    </td>
                    <td className={TD_NUM}><Money value={invoice.otherTaxBase} /></td>
                    <td className={TD_NUM}><Money value={invoice.vat} /></td>
                    <td className={TD_NUM}><Money value={invoice.luxuryTax} /></td>
                  </tr>
                  );
                })
              ) : (
                <NoRows colSpan={7}>{t("reports.vatRecap.output.none")}</NoRows>
              )}
            </tbody>
            {invoices.length > 0 && (
              <tfoot className="border-t border-gray-300 bg-gray-50 font-medium text-gray-900">
                <tr>
                  <th scope="row" colSpan={3} className={TH}>
                    {t("reports.vatRecap.output.total")}
                  </th>
                  <td className={TD_NUM}><Money value={totals.taxBase} /></td>
                  <td className={TD_NUM}><Money value={totals.otherTaxBase} /></td>
                  <td className={TD_NUM}><Money value={totals.vat} /></td>
                  <td className={TD_NUM}><Money value={totals.luxuryTax} /></td>
                </tr>
              </tfoot>
            )}
          </>
        )}
      </table>
    </Section>
  );
};

const INPUT_COLUMNS = [...OUTPUT_COLUMNS.slice(0, -1), { width: "w-16" }];

// No PPnBM column: luxury tax paid on a purchase cannot be credited, so it
// never reaches this return and a column of it would read as if it did.
export const InputTable = ({ bills = [], totals, period, loading }) => {
  const { t, i18n } = useTranslation();

  return (
    <Section
      title={t("reports.vatRecap.input.title")}
      hint={t("reports.vatRecap.input.hint")}
    >
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.date")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.billNumber")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.partner")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.taxBase")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.otherTaxBase")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.vat")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.creditPeriod")}</th>
          </tr>
        </thead>
        {loading ? (
          <TableSkeleton rows={4} columns={7} columnWidths={INPUT_COLUMNS} />
        ) : (
          <>
            <tbody className="divide-y divide-gray-200 bg-white">
              {bills.length ? (
                bills.map((bill) => {
                  const credited = isCredited(bill);
                  const creditPeriod = formatPeriod(
                    bill.vatCreditPeriod || period,
                    i18n.language,
                  );
                  return (
                    <tr
                      key={bill.id}
                      // Credited in another month: listed so the user can find
                      // it, muted and kept out of the total below.
                      className={credited ? "text-gray-700" : "text-gray-500"}
                    >
                      <td className={TD}>{formatRecapDate(bill.date)}</td>
                      <td className={TD}>
                        <Link
                          to={`/v2/accounting/expenses?invoiceId=${encodeURIComponent(bill.id)}`}
                          className={
                            credited
                              ? "font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              : "text-gray-500 underline decoration-gray-300 underline-offset-2 hover:text-gray-700"
                          }
                        >
                          {bill.invoiceNumber || "-"}
                        </Link>
                      </td>
                      <Partner partner={bill.partner} />
                      <td className={TD_NUM}><Money value={bill.taxBase} /></td>
                      <td className={TD_NUM}><Money value={bill.otherTaxBase} /></td>
                      <td className={TD_NUM}><Money value={bill.vat} /></td>
                      <td className={TD}>
                        {credited ? (
                          creditPeriod
                        ) : (
                          <Badge className={BADGE_NEUTRAL}>
                            {t("reports.vatRecap.input.creditedIn", {
                              period: creditPeriod,
                            })}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <NoRows colSpan={7}>{t("reports.vatRecap.input.none")}</NoRows>
              )}
            </tbody>
            {bills.length > 0 && (
              <tfoot className="border-t border-gray-300 bg-gray-50 font-medium text-gray-900">
                <tr>
                  <th scope="row" colSpan={3} className={TH}>
                    {t("reports.vatRecap.input.total")}
                  </th>
                  <td className={TD_NUM}><Money value={totals.taxBase} /></td>
                  <td className={TD_NUM}><Money value={totals.otherTaxBase} /></td>
                  <td className={TD_NUM}><Money value={totals.vat} /></td>
                  <td className={TD} />
                </tr>
              </tfoot>
            )}
          </>
        )}
      </table>
    </Section>
  );
};

// Entries posted straight to a VAT account without an invoice or bill. They
// count toward the figure, so they get a table; none, and it is left out.
export const JournalTable = ({ title, journals = [] }) => {
  const { t } = useTranslation();
  if (!journals.length) return null;

  return (
    <Section title={title} as="h4">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.date")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.description")}</th>
            <th scope="col" className={TH}>{t("reports.vatRecap.columns.account")}</th>
            <th scope="col" className={TH_NUM}>{t("reports.vatRecap.columns.amount")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white text-gray-700">
          {journals.map((journal, index) => {
            const description = journal.description || journal.type || "-";
            return (
              <tr key={`${journal.transactionID}-${index}`}>
                <td className={`${TD} align-top`}>{formatRecapDate(journal.date)}</td>
                <td className="min-w-[14rem] px-4 py-2 align-top text-sm">
                  <span
                    title={description}
                    className="line-clamp-2 [overflow-wrap:anywhere]"
                  >
                    {description}
                  </span>
                </td>
                <td className={`${TD} align-top`}>{accountLabel(journal.account)}</td>
                <td className={`${TD_NUM} align-top`}>
                  <Money value={journal.amount} />
                </td>
              </tr>
            );
          })}
        </tbody>
        {journals.length > 1 && (
          <tfoot className="border-t border-gray-300 bg-gray-50 font-medium text-gray-900">
            <tr>
              <th scope="row" colSpan={3} className={TH}>
                {t("reports.vatRecap.journals.total")}
              </th>
              <td className={TD_NUM}><Money value={sumAmounts(journals)} /></td>
            </tr>
          </tfoot>
        )}
      </table>
    </Section>
  );
};

/* ------------------------------------------------------------ Empty state */

export const EmptyRecap = ({ period }) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
      <DocumentReportIcon
        className="mx-auto h-10 w-10 text-gray-400"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <h3 className="mt-2 text-sm font-semibold text-gray-900">
        {t("reports.vatRecap.empty.title", {
          period: formatPeriod(period, i18n.language, "MMMM YYYY"),
        })}
      </h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600">
        {t("reports.vatRecap.empty.body")}
      </p>
      <Link
        to="/v2/accounting/incomes?new=invoice"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <PlusIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
        {t("reports.vatRecap.empty.action")}
      </Link>
    </div>
  );
};
